import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Priority, Report } from "@/types";
import { reportService } from "@/services/reportService";
import { FileText, AlertCircle, Calendar, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { UserAvatar } from "@/components/UserAvatar";

interface PriorityReportsModalProps {
  priority: Priority | null;
  userName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PriorityReportsModal = ({
  priority,
  userName,
  open,
  onOpenChange,
}: PriorityReportsModalProps) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !priority) {
      setReports([]);
      setError(null);
      return;
    }

    const abortController = new AbortController();

    const fetchReports = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await reportService.getReports(1, 100, {
          priorityPid: priority.pid,
        });

        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          setReports(response.data);
        }
      } catch (err) {
        // Only update error state if not aborted
        if (!abortController.signal.aborted) {
          console.error("Failed to fetch reports:", err);
          setError("Failed to load reports");
        }
      } finally {
        // Only update loading state if not aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchReports();

    // Cleanup: abort the request when component unmounts or dependencies change
    return () => {
      abortController.abort();
    };
  }, [open, priority]);

  if (!priority) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold mb-1">{priority.name}</div>
              <div className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
                <Badge variant="outline" className="font-mono">
                  {priority.code}
                </Badge>
                <span>•</span>
                <span>{userName}</span>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Separator />

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <Skeleton className="h-3 w-12" />
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold mb-2">No Reports Yet</h3>
            <p className="text-sm text-muted-foreground">
              No reports have been submitted for this priority.
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[50vh]">
            <div className="space-y-4 pr-4">
              {reports.map((report) => (
                <div
                  key={report.pid}
                  className="rounded-lg border border-border bg-card p-4 space-y-3"
                >
                  {/* Report Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        src={report.user?.avatarUrl}
                        name={report.user?.fullName}
                        className="h-8 w-8"
                      />
                      <div>
                        <div className="font-medium text-sm">
                          {report.user?.fullName}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {format(new Date(report.reportDate), "MMM d, yyyy")}
                          </span>
                        </div>
                      </div>
                    </div>
                    {report.blocker && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Blocker
                      </Badge>
                    )}
                  </div>

                  {/* Report Content */}
                  <div className="text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-1.5">
                      <TrendingUp className="h-3 w-3" />
                      <span className="text-xs font-medium uppercase tracking-wide">Update</span>
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">{report.content}</div>
                  </div>

                  {/* Blocker Details */}
                  {report.blocker && report.blockedReason && (
                    <div className="text-sm rounded-md bg-destructive/5 border border-destructive/20 p-3">
                      <div className="text-destructive font-medium mb-1">
                        Blocker Details:
                      </div>
                      <div className="text-muted-foreground whitespace-pre-wrap">
                        {report.blockedReason}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
